// Infinite, auto-advancing horizontal slider.
// Each slider's slide list is duplicated once so the track is [originals, clones].
// When the index crosses past the originals it snaps (no transition) back to the
// equivalent position in the original block — invisible to the viewer because the
// clones look identical at that moment. Same trick in reverse for the prev button.
// Hover/focus pauses the auto-advance.
export default function initImageSlider() {
    const AUTO_MS = 5000;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-image-slider]').forEach((root) => {
        const list = root.querySelector('[data-image-slider-list]');
        const prev = root.querySelector('[data-image-slider-prev]');
        const next = root.querySelector('[data-image-slider-next]');
        if (!list || !prev || !next) return;

        const originals = Array.from(list.children);
        if (originals.length === 0) return;

        // Duplicate the slide set once so a single forward step past the end
        // can be reverse-snapped to the same-looking start without flicker.
        originals.forEach((slide) => list.appendChild(slide.cloneNode(true)));
        const total = originals.length;

        let index = 0;
        let animating = false;
        let timer = null;

        const slideWidth = () => list.children[0].getBoundingClientRect().width;

        const setTransform = (animated) => {
            list.style.transition = animated ? '' : 'none';
            list.style.transform = `translate3d(-${index * slideWidth()}px, 0, 0)`;
            if (!animated) {
                // Force reflow then re-enable transitions on the next frame.
                // eslint-disable-next-line no-unused-expressions
                list.offsetHeight;
                requestAnimationFrame(() => { list.style.transition = ''; });
            }
        };

        const goTo = (i) => {
            if (animating) return;
            animating = true;
            index = i;
            setTransform(true);
        };

        list.addEventListener('transitionend', () => {
            animating = false;
            // Past the originals — silently rewind to the matching original slot.
            if (index >= total) {
                index = index - total;
                setTransform(false);
            } else if (index < 0) {
                index = index + total;
                setTransform(false);
            }
        });

        const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
        const start = () => {
            stop();
            if (reduceMotion) return;
            timer = setInterval(() => goTo(index + 1), AUTO_MS);
        };

        prev.addEventListener('click', () => { stop(); goTo(index - 1); start(); });
        next.addEventListener('click', () => { stop(); goTo(index + 1); start(); });

        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', start);
        root.addEventListener('focusin', stop);
        root.addEventListener('focusout', start);
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) stop(); else start();
        });

        window.addEventListener('resize', () => {
            // Re-pin the current index after layout shifts.
            requestAnimationFrame(() => setTransform(false));
        }, { passive: true });

        setTransform(false);
        start();
    });
}
